import { Injectable, Signal, inject, signal } from '@angular/core'
import {
  signInWithRedirect,
  signOut,
  fetchAuthSession,
  updatePassword,
} from 'aws-amplify/auth'
import type { UserRole } from '../models/models'
import { ActivityTrackerService } from '../activity/activity-tracker.service'

/**
 * The subset of JWT ID-token claims we care about.
 * Cognito always populates `sub` and `email`.
 * `name` is populated because we mark it as required in the User Pool (DevStack).
 * `cognito:groups` lists all groups the user belongs to — these map to app roles.
 *
 * The JWT payload is typed as `Record<string, unknown>` by Amplify, so we cast
 * each field explicitly.
 */
export interface CognitoUser {
  /** Cognito user sub — stable UUID for this user across all tokens. */
  id: string
  email: string
  name: string
  /** Groups from the `cognito:groups` JWT claim — maps to application roles. */
  roles: UserRole[]
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly activity = inject(ActivityTrackerService)
  private readonly signingOutState = signal(false)
  /**
   * True from the start of {@link signOut} until the browser leaves for the
   * Cognito logout page (false again if that fails): screens that warn before
   * the page unloads (unapplied edits) stay quiet for a sign-out.
   */
  readonly signingOut: Signal<boolean> = this.signingOutState.asReadonly()

  /**
   * Redirects the browser to the Cognito Hosted UI login page.
   *
   * Amplify handles PKCE automatically:
   *   1. Generates a random code_verifier, stores it in sessionStorage.
   *   2. Computes code_challenge = SHA-256(code_verifier).
   *   3. Redirects to: https://<domain>/login?...&code_challenge=...
   *
   * After the user logs in, Cognito redirects to /auth/callback?code=<auth_code>.
   * The AuthCallbackComponent then calls resolveCurrentUser() to exchange the
   * code for tokens and extract the user identity.
   */
  async signIn(): Promise<void> {
    await signInWithRedirect()
  }

  /**
   * Redirects straight to Google (skipping the Hosted UI account chooser) via the
   * configured Google identity provider. Used by the "Sign in with Google" button.
   *
   * Only @neotreks.com accounts are accepted: the Cognito Pre-Sign-Up trigger
   * rejects other domains, which surfaces as a `signInWithRedirect_failure` Hub
   * event handled by AuthCallbackComponent (it routes the user back to
   * /login?error=domain). Brand-new @neotreks.com users are auto-provisioned with
   * the Viewer role by the Pre-Token-Generation trigger before their first token
   * is issued.
   */
  async signInWithGoogle(): Promise<void> {
    await signInWithRedirect({ provider: 'Google' })
  }

  /**
   * Signs the user out and redirects to the Cognito logout endpoint,
   * which then redirects to the app's configured logoutUrl (/login).
   *
   * Amplify clears all locally stored tokens as part of this call.
   * If the Amplify call fails (network error, config issue) we perform
   * best-effort localStorage cleanup before rethrowing so that tokens
   * do not linger while the app believes the user is logged out.
   */
  async signOut(): Promise<void> {
    this.signingOutState.set(true)
    // Editor Activity Tracking: last events out (bounded by its own 3 s race), then a purge of everything stored.
    await this.activity.onSignOut().catch(() => undefined)
    try {
      await signOut()
    } catch (e) {
      // Best-effort cleanup so tokens don't linger if the Cognito redirect failed
      try { localStorage.clear() } catch {}
      this.signingOutState.set(false)
      throw e
    }
  }

  /**
   * Returns true if there is a valid (non-expired) authenticated session.
   *
   * Amplify automatically uses the stored refresh token to renew the access
   * token silently if it has expired. Returns false if the refresh token is
   * also expired or if the user has never logged in.
   *
   * Used by auth.guard.ts to gate protected routes.
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession()
      // `tokens` is undefined when there is no active session
      return !!session.tokens?.idToken
    } catch {
      return false
    }
  }

  /**
   * Fetches the current Cognito session and decodes the ID token to produce
   * a CognitoUser object.
   *
   * Returns null if the session is invalid or the token cannot be decoded.
   *
   * Called by:
   *   - AuthCallbackComponent after the Hosted UI redirect completes.
   *   - auth.guard when the user reloads the page mid-session (the session is
   *     still valid in localStorage but currentUser is lost from memory).
   */
  /**
   * Changes the currently signed-in user's password.
   * Requires the user's current password for verification.
   * Throws if the old password is incorrect or the new password violates Cognito's policy.
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword, newPassword })
  }

  async resolveCurrentUser(): Promise<CognitoUser | null> {
    try {
      const session = await fetchAuthSession()
      const payload = session.tokens?.idToken?.payload
      if (!payload) return null

      // `sub` is always a string in Cognito ID tokens.
      const id    = payload['sub'] as string
      const email = payload['email'] as string
      // `name` is populated because we mark it required in the User Pool.
      // Fallback to email so the UI always has something to display.
      const name  = (payload['name'] as string | undefined) ?? email

      // `cognito:groups` is an array of the group names the user belongs to.
      // It is absent from the claim if the user is in no groups.
      const rawGroups = (payload['cognito:groups'] as string[] | undefined) ?? []

      // Filter to only the known role values; ignore any unrecognised groups.
      const knownRoles: UserRole[] = ['Admin', 'Editor', 'Reviewer', 'Viewer', 'EditorSupervisor']
      const roles = rawGroups.filter((g): g is UserRole => knownRoles.includes(g as UserRole))

      return { id, email, name, roles }
    } catch {
      return null
    }
  }
}
